import {makeAutoObservable, runInAction} from 'mobx';
import GradebookRepository from '../../../../data/repositories/GradebookRepository';
import OrganizationRepository from '../../../../data/repositories/OrganizationRepository';
import LoadingModule from '../../../../modules/LoadingModule/LoadingModule';
import {
  AvgGradeLearnerListModel,
  AvgLearnerGradeModel,
  CourseAvgGradesModel,
  LearnerAvgGradesModel,
} from '../../../../data/models/Gradebook/LearnerAvgGradesModel';
import {LearnerStatistics} from '../../../../data/models/Workspace/Dashboard';
import {toast} from 'react-toastify';
import {getErrorFromCode} from '../../../../data/errorCodes';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {EXPORT_PAGE_SIZE, LEARNERS_PER_PAGE} from '../conf';

class AnalyticsStore {
  private gradebookRepo = new GradebookRepository();
  private organizationRepo = new OrganizationRepository();
  private loadingModule = new LoadingModule();

  private coursesData?: CourseAvgGradesModel;
  private statistics?: LearnerStatistics;
  private learners: AvgGradeLearnerListModel[] = [];
  private grades: {[key: string]: AvgLearnerGradeModel[]} = {};
  private pagesCount = 0;
  // Loaded flags per widget so the page can show independent preloaders:
  // false until the first response (success or error) comes back.
  private coursesLoaded = false;
  private learnersLoaded = false;
  private statisticsLoaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public get hasLoadedCourses() {
    return this.coursesLoaded;
  }

  public get hasLoadedLearners() {
    return this.learnersLoaded;
  }

  public get hasLoadedStatistics() {
    return this.statisticsLoaded;
  }

  public get learnerStatistics() {
    return this.statistics;
  }

  public get dataIsReady() {
    return (
      this.coursesData != null &&
      this.coursesData.courses.length > 0 &&
      this.learners.length > 0
    );
  }

  public get coursesList() {
    if (this.coursesData) {
      return this.coursesData.courses;
    }
    return [];
  }

  public get learnersList() {
    return this.learners;
  }

  public get gradesMap() {
    return this.grades;
  }

  public get coursesAnalytics() {
    return this.coursesData;
  }

  public async getCoursesList(organizationId: string) {
    this.loadingModule.startLoading();
    const result = await this.gradebookRepo.getCoursesAvgGrades(organizationId);
    this.loadingModule.endLoading();
    runInAction(() => {
      this.coursesLoaded = true;
      if (result.success && result.data) {
        this.coursesData = result.data;
      } else if (result.errors && result.errors.length) {
        toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      }
    });
  }

  public async getLearnerStatistics(organizationId: string) {
    const result = await this.organizationRepo.getLearnerStatistics(
      organizationId,
      new Date(0).toISOString(),
      new Date().toISOString(),
    );
    runInAction(() => {
      this.statisticsLoaded = true;
      if (result.success && result.data) {
        this.statistics = result.data;
      } else if (result.errors && result.errors.length) {
        toast.error(getErrorFromCode(result.errors[0]), toastConfig);
      }
    });
  }

  public get learnersPagesCount() {
    return this.pagesCount;
  }

  public async getLearnersPage(
    organizationId: string,
    page: number,
    search: string,
    orderAsc: boolean,
  ) {
    this.loadingModule.startLoading();
    const result = await this.gradebookRepo.getLearnersWithAvgScores(
      organizationId,
      page,
      LEARNERS_PER_PAGE,
      search,
      orderAsc,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.data;
  }

  public setLearnersPage(data?: LearnerAvgGradesModel) {
    this.learnersLoaded = true;
    if (data) {
      this.learners = data.learners;
      this.grades = data.grades;
      this.pagesCount = data.pagesCount;
    }
  }

  public async getAllLearners(
    organizationId: string,
    search: string,
    orderAsc: boolean,
  ) {
    const loadPage = (page: number) =>
      this.gradebookRepo.getLearnersWithAvgScores(
        organizationId,
        page,
        EXPORT_PAGE_SIZE,
        search,
        orderAsc,
      );
    const first = await loadPage(0);
    const pagesCount = first.data?.pagesCount || 0;
    const rest = await Promise.all(
      Array.from({length: Math.max(pagesCount - 1, 0)}, (_, index) =>
        loadPage(index + 1),
      ),
    );
    const results = [first, ...rest];
    const failed = results.find(result => !result.success);
    if (failed) {
      if (failed.errors && failed.errors.length) {
        toast.error(getErrorFromCode(failed.errors[0]), toastConfig);
      }
      return undefined;
    }
    return {
      learners: results.flatMap(result => result.data?.learners || []),
      grades: results.reduce(
        (acc, result) => ({...acc, ...result.data?.grades}),
        {} as {[key: string]: AvgLearnerGradeModel[]},
      ),
    };
  }
}

export default AnalyticsStore;
